import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { transactionAPI, departmentAPI } from '@/services/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { History, Search, Filter, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { toast } from 'sonner';

const Transactions = () => {
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filters, setFilters] = useState({ department_id: 'all', status: 'all', search: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [txnRes, deptRes] = await Promise.all([
        transactionAPI.list({ limit: 100 }),
        departmentAPI.list()
      ]);
      setTransactions(txnRes.data);
      setDepartments(deptRes.data);
    } catch (err) {
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount || 0);
  };

  const getStatusStyle = (status) => {
    const styles = {
      'COMPLETED': 'status-success',
      'PENDING_APPROVAL': 'status-pending',
      'REJECTED': 'status-error',
      'PROCESSING': 'status-info',
      'INFO_REQUESTED': 'status-pending',
      'CANCELLED': 'status-error'
    };
    return styles[status] || 'status-info';
  };

  const filteredTransactions = transactions.filter(txn => {
    if (filters.department_id !== 'all' && txn.department_id !== filters.department_id) return false;
    if (filters.status !== 'all' && txn.status !== filters.status) return false;
    if (filters.search && !txn.beneficiary_name.toLowerCase().includes(filters.search.toLowerCase())) return false;
    return true;
  });

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="transactions-page">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 font-['Outfit']">Transaction History</h1>
          <p className="text-gray-500 mt-1">View all transactions across your organization</p>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <Input
              value={filters.search}
              onChange={(e) => setFilters({...filters, search: e.target.value})}
              placeholder="Search beneficiary..."
              className="pl-10 bg-white/50 border-gray-200 text-gray-900"
            />
          </div>
          <Select value={filters.department_id} onValueChange={(val) => setFilters({...filters, department_id: val})}>
            <SelectTrigger className="w-48 bg-white/50 border-gray-200 text-gray-900">
              <SelectValue placeholder="Department" />
            </SelectTrigger>
            <SelectContent className="bg-gray-50 border-gray-300">
              <SelectItem value="all" className="text-gray-900">All Departments</SelectItem>
              {departments.map(d => <SelectItem key={d.department_id} value={d.department_id} className="text-gray-900">{d.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filters.status} onValueChange={(val) => setFilters({...filters, status: val})}>
            <SelectTrigger className="w-48 bg-white/50 border-gray-200 text-gray-900">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="bg-gray-50 border-gray-300">
              <SelectItem value="all" className="text-gray-900">All Status</SelectItem>
              <SelectItem value="COMPLETED" className="text-gray-900">Completed</SelectItem>
              <SelectItem value="PENDING_APPROVAL" className="text-gray-900">Pending</SelectItem>
              <SelectItem value="REJECTED" className="text-gray-900">Rejected</SelectItem>
              <SelectItem value="PROCESSING" className="text-gray-900">Processing</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Transactions Table */}
        <Card className="glass-card">
          <CardContent className="p-0">
            {filteredTransactions.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-sm text-gray-500 border-b border-gray-200">
                      <th className="p-4 font-medium">Date</th>
                      <th className="p-4 font-medium">Beneficiary</th>
                      <th className="p-4 font-medium">Department</th>
                      <th className="p-4 font-medium">Amount</th>
                      <th className="p-4 font-medium">Status</th>
                      <th className="p-4 font-medium">Submitted By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTransactions.map((txn) => (
                      <tr key={txn.transaction_id} className="border-b border-gray-200/50 table-row-hover">
                        <td className="p-4 text-sm text-gray-500">
                          {new Date(txn.submitted_at).toLocaleDateString()}
                        </td>
                        <td className="p-4">
                          <p className="text-gray-900 font-medium">{txn.beneficiary_name}</p>
                          <p className="text-xs text-gray-500 font-['JetBrains_Mono']">{txn.beneficiary_details}</p>
                        </td>
                        <td className="p-4 text-gray-500">{txn.department_name}</td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            {txn.transaction_type === 'SEND' ? (
                              <ArrowUpRight className="w-4 h-4 text-red-400" />
                            ) : (
                              <ArrowDownRight className="w-4 h-4 text-blue-500" />
                            )}
                            <span className="font-['JetBrains_Mono'] text-gray-900">{formatCurrency(txn.amount)}</span>
                          </div>
                        </td>
                        <td className="p-4">
                          <Badge className={getStatusStyle(txn.status)}>
                            {txn.status.replace(/_/g, ' ')}
                          </Badge>
                        </td>
                        <td className="p-4 text-gray-500 text-sm">{txn.submitted_by_name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16">
                <History className="w-16 h-16 text-gray-300 mb-4" />
                <p className="text-gray-500">No transactions found</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default Transactions;
